import { FetchError } from '@execonline-inc/fetch.private';
import { toTask } from '@execonline-inc/maybe-adapter';
import { always } from '@kofno/piper';
import { just, Maybe, nothing } from 'maybeasy';
import { Task } from 'taskarian';
import { AppyError } from '../../../../../Appy';
import { currentUserStore } from '../../../../../CurrentUserStore';
import fetch from '../../../../../Fetch/Init';
import { declareEndpoint, findLinkByT, MissingLinkError } from '../../../../../Links';
import * as Resourceable from '../../../../../Resourceable';
import ResourceableInit from '../../../../../Resourceable/Init';
import { createLearningCollectionResourceDecoder } from '../../CurrentCollectionStep/BuilderV2/Decoder';
import { collectionTitleTimeStamp } from '../Types';
import { previewLearningCollectionEncoder } from './PreviewLearningCollectionEncoder';

export interface PreviewStreamBase {
  turnUUID: string;
  seq: Maybe<number>;
}

export interface StreamedPreview extends PreviewStreamBase {
  type: 'preview';
  name: string;
  description: string;
  recommendedProgramFamilyIds: number[];
}

export interface PreviewEndOfStream extends PreviewStreamBase {
  type: 'end-of-stream';
}

export type StreamedPreviewMessage = StreamedPreview | PreviewEndOfStream;

export type PreviewError = FetchError | MissingLinkError | AppyError | MissingIdError;

interface MissingIdError {
  kind: 'missing-id-error';
  message: string;
}

export type PreviewResource = Resourceable.ResourceFor<
  '/preview_learning_collection_template',
  'get',
  200
>;

export interface Ready {
  kind: 'ready';
  resource: PreviewResource;
  internalName: Maybe<string>;
}

export interface RecommendationsIncoming {
  kind: 'recommendations-incoming';
  resource: PreviewResource;
  internalName: Maybe<string>;
}

export interface Loading {
  kind: 'loading';
}

export interface Error {
  kind: 'error';
  message: string;
}

export interface Publishing {
  kind: 'publishing';
  resource: PreviewResource;
  internalName: Maybe<string>;
}

export interface Creating {
  kind: 'creating';
  resource: PreviewResource;
  internalName: Maybe<string>;
}

export interface SaveAndEditing {
  kind: 'save-and-editing';
  resource: PreviewResource;
  internalName: Maybe<string>;
}

export interface ConfirmingCancel {
  kind: 'confirming-cancel';
  resource: PreviewResource;
  internalName: Maybe<string>;
}

export interface ConfirmingCancelFromPublish {
  kind: 'confirming-cancel-from-publish';
  resource: PreviewResource;
  internalName: Maybe<string>;
}

export interface ConfirmingCancelFromCreate {
  kind: 'confirming-cancel-from-create';
  resource: PreviewResource;
  internalName: Maybe<string>;
}

export interface ConfirmingCancelFromSaveAndEdit {
  kind: 'confirming-cancel-from-save-and-edit';
  resource: PreviewResource;
  internalName: Maybe<string>;
}

export interface Waiting {
  kind: 'waiting';
}

export interface SavingFromPublishing {
  kind: 'saving-from-publishing';
  resource: PreviewResource;
  internalName: Maybe<string>;
}

export interface SavingFromCreating {
  kind: 'saving-from-creating';
  resource: PreviewResource;
  internalName: Maybe<string>;
}

export interface SavingFromSaveAndEditing {
  kind: 'saving-from-save-and-editing';
  resource: PreviewResource;
  internalName: Maybe<string>;
}

export interface PublishCelebration {
  kind: 'publish-celebration';
}

export interface SaveCelebration {
  kind: 'save-celebration';
}

export interface DiscardingChanges {
  kind: 'discarding-changes';
  destination: string;
}

export type PreviewState =
  | Waiting
  | Loading
  | Ready
  | RecommendationsIncoming
  | Error
  | Publishing
  | Creating
  | SaveAndEditing
  | ConfirmingCancel
  | ConfirmingCancelFromPublish
  | ConfirmingCancelFromCreate
  | ConfirmingCancelFromSaveAndEdit
  | SavingFromPublishing
  | SavingFromCreating
  | SavingFromSaveAndEditing
  | PublishCelebration
  | SaveCelebration
  | DiscardingChanges;

const errorMessage = (error: PreviewError): string => {
  switch (error.kind) {
    case 'missing-id-error':
      return error.message;
    case 'missing-link-error':
      return `${error.rel} link is missing`;
    default:
      return error.kind;
  }
};

export const ready = (
  resource: PreviewResource,
  internalName: Maybe<string>,
  state: PreviewState,
): Ready => ({
  kind: 'ready',
  resource: {
    ...resource,
    payload: {
      ...resource.payload,
      name: just(collectionTitleTimeStamp()),
    },
  },
  internalName: internalName,
});

export const fetchLearningCollectionPreviewTemplate = (): Task<PreviewError, PreviewResource> => {
  const findLinkAndDeclare = findLinkByT(
    { rel: 'preview-learning-collection-template-v2', method: 'get', type: 'application/json' },
    currentUserStore.links,
  ).map(declareEndpoint('/preview_learning_collection_template'));

  return Task.succeed<PreviewError, {}>({})
    .andThen(always(findLinkAndDeclare))
    .andThen(ResourceableInit.fetch)
    .map(({ resource }) => resource);
};

export const waiting = (): Waiting => ({
  kind: 'waiting',
});

export const loading = (): Loading => ({
  kind: 'loading',
});

export const error = (error: PreviewError): Error => ({
  kind: 'error',
  message: errorMessage(error),
});

export const savingFromPublishing = (state: Publishing): SavingFromPublishing => ({
  kind: 'saving-from-publishing',
  resource: state.resource,
  internalName: nothing(),
});

export const savingFromCreating = (state: Creating): SavingFromCreating => ({
  kind: 'saving-from-creating',
  resource: state.resource,
  internalName: nothing(),
});

export const savingFromSaveAndEditing = (state: SaveAndEditing): SavingFromSaveAndEditing => ({
  kind: 'saving-from-save-and-editing',
  resource: state.resource,
  internalName: nothing(),
});

export const publishCelebration = (): PublishCelebration => ({
  kind: 'publish-celebration',
});

export const saveCelebration = (): SaveCelebration => ({
  kind: 'save-celebration',
});

export const discardingChanges = (destination: string): DiscardingChanges => ({
  kind: 'discarding-changes',
  destination,
});

export const publishing = (state: Ready | ConfirmingCancelFromPublish): Publishing => ({
  kind: 'publishing',
  resource: state.resource,
  internalName: state.internalName,
});

export const creating = (state: Ready | ConfirmingCancelFromCreate): Creating => ({
  kind: 'creating',
  resource: state.resource,
  internalName: state.internalName,
});

export const saveAndEditing = (state: Ready | ConfirmingCancelFromSaveAndEdit): SaveAndEditing => ({
  kind: 'save-and-editing',
  resource: state.resource,
  internalName: state.internalName,
});

export const confirmingCancel = (state: Ready): ConfirmingCancel => ({
  kind: 'confirming-cancel',
  resource: state.resource,
  internalName: state.internalName,
});

export const confirmingCancelFromPublish = (state: Publishing): ConfirmingCancelFromPublish => ({
  kind: 'confirming-cancel-from-publish',
  resource: state.resource,
  internalName: state.internalName,
});

export const confirmingCancelFromCreate = (state: Creating): ConfirmingCancelFromCreate => ({
  kind: 'confirming-cancel-from-create',
  resource: state.resource,
  internalName: state.internalName,
});

export const confirmingCancelFromSaveAndEdit = (
  state: SaveAndEditing,
): ConfirmingCancelFromSaveAndEdit => ({
  kind: 'confirming-cancel-from-save-and-edit',
  resource: state.resource,
  internalName: state.internalName,
});

export const createCollectionT = (
  resource: PreviewResource,
  kind: 'publishing' | 'creating' | 'save-and-editing',
  internalName: Maybe<string>,
) =>
  Task.succeed<PreviewError, {}>({})
    .andThen(() =>
      findLinkByT(
        {
          rel: 'create-learning-collection-template-v2',
          method: 'post',
          type: 'application/json',
        },
        currentUserStore.links,
      ),
    )
    .andThen((link) =>
      fetch.post(link.href, createLearningCollectionResourceDecoder, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(previewLearningCollectionEncoder(resource, kind, internalName)),
      }),
    )
    .andThen(({ payload }) =>
      toTask(
        {
          kind: 'missing-id-error',
          message: 'payload.id from preview-learning-collection-template-v2 endpoint not found',
        },
        payload.id,
      ),
    );
